// Shift-drag to reorder a set of labelled siblings — the table's header row,
// and the column picker's "Shown" list.
//
// Deliberately pointer events and NOT HTML5 drag-and-drop. What you actually
// grab in a header is the sort <button>, and in the picker a <label> wrapping a
// checkbox; no browser will begin a native drag from a form control, so
// `draggable` there is set and then never honoured. Pointer events have no such
// veto, work the same on touch, and — unlike `dragstart`, which the browser
// decides to fire on its own — can be driven end to end from a test.
//
// Shift arms the gesture so a plain click still reaches the sort button and the
// labels stay selectable text. Arming toggles a class rather than doing
// anything itself, because the cursor is the only thing that tells you the
// gesture exists.
//
// Only the drop costs a round trip: moving a column re-parents one element
// among its siblings, entirely here. The push sends the full label list rather
// than a from/to pair, so the result never depends on the server and the client
// agreeing about where the column started.
//
// Structure it reads, all on the container:
//   [data-label]         — each movable child, carrying its column label
//   [data-reorder-event] — the event name to push
//   [data-orientation]   — "horizontal" for a header row; vertical otherwise
const ROW = "[data-label]";
const ARMED = "is-reorder-armed";
const DRAGGING = "is-reordering";

// The band at each end of the scrollport that pulls, and the fastest it pulls
// (px per frame — ~60 of them a second).
const EDGE_PX = 64;
const MAX_SPEED_PX = 22;

/**
 * How far to scroll this frame for a pointer at `pos` within a scrollport
 * spanning `min`..`max`. Zero through the middle, ramping to `speed` at the
 * very edge, so a shallow approach creeps and the edge itself moves quickly.
 * Past the edge entirely — the pointer has left the scrollport — it stays at
 * full speed rather than reversing.
 */
export function edgeScrollDelta(pos, min, max, edge = EDGE_PX, speed = MAX_SPEED_PX) {
  if (pos < min + edge) return -ramp(min + edge - pos, edge, speed);
  if (pos > max - edge) return ramp(pos - (max - edge), edge, speed);
  return 0;
}

function ramp(depth, edge, speed) {
  return Math.ceil((Math.min(depth, edge) / edge) * speed);
}

// The scrollport to pull on: the nearest ancestor that both can scroll on our
// axis and currently overflows. Found per drag rather than at mount, since a
// table only starts overflowing once enough columns are on.
function scrollportFor(el, horizontal) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    const overflow = horizontal ? style.overflowX : style.overflowY;
    if (!/(auto|scroll)/.test(overflow)) continue;

    const overflows = horizontal
      ? node.scrollWidth > node.clientWidth
      : node.scrollHeight > node.clientHeight;

    if (overflows) return node;
  }

  return null;
}

export const ColumnOrder = {
  mounted() {
    this.dragging = null;
    this.scrollport = null;
    this.point = null;
    this.frame = null;

    this.horizontal = () => this.el.dataset.orientation === "horizontal";

    this.onKey = (event) => this.el.classList.toggle(ARMED, event.shiftKey === true);
    // A Shift held while switching windows would otherwise leave a grab cursor
    // with nothing behind it.
    this.onBlur = () => this.el.classList.remove(ARMED);

    // Re-parents the dragged column to wherever the pointer is. Taken as
    // coordinates rather than an event so the auto-scroll can re-run it: the
    // columns move under a stationary pointer while the scrollport slides, and
    // without a fresh hit test the drop target goes stale.
    this.moveTo = (x, y) => {
      const over = document.elementFromPoint(x, y)?.closest(ROW);
      if (!over || over === this.dragging || !this.el.contains(over)) return;

      // Past the midpoint means the column belongs after this one. Anchoring on
      // the midpoint rather than the edge keeps a column from flipping back and
      // forth while the pointer sits still between two of them.
      const rect = over.getBoundingClientRect();
      const after = this.horizontal()
        ? x > rect.left + rect.width / 2
        : y > rect.top + rect.height / 2;

      // Only the header moves during the drag; the cells below follow when the
      // server re-renders. ponytail: a drop indicator would avoid the momentary
      // mismatch, at the cost of positioning one inside a scrolling table.
      over.parentNode.insertBefore(this.dragging, after ? over.nextSibling : over);
    };

    // A drag that reaches the edge has to be able to keep going: with the
    // columns this table carries, the far end is well past the scrollport, and
    // a pointer can only travel as far as the window.
    this.pull = () => {
      this.frame = requestAnimationFrame(this.pull);
      if (!this.dragging || !this.scrollport || !this.point) return;

      const horizontal = this.horizontal();
      const rect = this.scrollport.getBoundingClientRect();

      const delta = horizontal
        ? edgeScrollDelta(this.point.x, rect.left, rect.right)
        : edgeScrollDelta(this.point.y, rect.top, rect.bottom);

      if (delta === 0) return;

      if (horizontal) {
        this.scrollport.scrollLeft += delta;
      } else {
        this.scrollport.scrollTop += delta;
      }

      this.moveTo(this.point.x, this.point.y);
    };

    this.onPointerDown = (event) => {
      if (!event.shiftKey || event.button !== 0) return;

      const row = event.target.closest(ROW);
      if (!row || !this.el.contains(row)) return;

      // Suppresses the text selection and the compatibility click that would
      // otherwise sort the column out from under the drag.
      event.preventDefault();

      this.dragging = row;
      this.point = { x: event.clientX, y: event.clientY };
      this.scrollport = scrollportFor(this.el, this.horizontal());
      this.el.classList.add(DRAGGING);
      row.dataset.dragging = "";

      this.frame = requestAnimationFrame(this.pull);
    };

    this.onPointerMove = (event) => {
      if (!this.dragging) return;

      this.point = { x: event.clientX, y: event.clientY };
      this.moveTo(event.clientX, event.clientY);
    };

    // Also the `pointercancel` handler: the columns have already moved by then,
    // so reporting the order on screen is right either way. An unchanged order
    // costs one patch that re-renders nothing.
    this.onPointerUp = () => {
      if (!this.dragging) return;

      cancelAnimationFrame(this.frame);
      this.frame = null;

      delete this.dragging.dataset.dragging;
      this.dragging = null;
      this.scrollport = null;
      this.point = null;
      this.el.classList.remove(DRAGGING);

      const name = this.el.dataset.reorderEvent;
      if (!name) return;

      const labels = Array.from(this.el.querySelectorAll(ROW), (row) => row.dataset.label);
      this.pushEvent(name, { labels });
    };

    this.el.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
    window.addEventListener("keydown", this.onKey);
    window.addEventListener("keyup", this.onKey);
    window.addEventListener("blur", this.onBlur);
  },

  destroyed() {
    if (this.frame) cancelAnimationFrame(this.frame);

    this.el.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("keyup", this.onKey);
    window.removeEventListener("blur", this.onBlur);
  },
};
